import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  CookieConsentState,
  getConsentState,
  setConsentState,
  isGPCEnabled,
  injectTier3Scripts,
  injectTier2Scripts,
  revokeTier3,
} from '@/lib/cookie-consent-core';

interface CookieConsentContextType {
  consent: CookieConsentState | null;
  showBanner: boolean;
  showSettings: boolean;
  gpcDetected: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCustom: (tier2: boolean, tier3: boolean) => void;
  openSettings: () => void;
  closeSettings: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export function useConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useConsent must be used within CookieConsentProvider');
  return ctx;
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const gpcDetected = isGPCEnabled();

  // Read initial state from the inline <head> script's hydration or localStorage
  const [consent, setConsent] = useState<CookieConsentState | null>(() => {
    // The inline script in index.html may have set window.__COOKIE_CONSENT__
    const hydrated = (window as any).__COOKIE_CONSENT__ as CookieConsentState | null;
    return hydrated ?? getConsentState();
  });

  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Show banner if no valid consent exists, or if GPC auto-resolved (informational)
  useEffect(() => {
    if (!consent || consent.method === 'gpc_auto') {
      setShowBanner(true);
    }
  }, [consent]);

  const applyConsent = useCallback(
    (state: CookieConsentState) => {
      setConsent(state);
      setShowBanner(false);
      setShowSettings(false);

      // Inject or revoke scripts based on new consent
      if (state.tier3) {
        injectTier3Scripts();
      } else {
        revokeTier3();
      }
      if (state.tier2) {
        injectTier2Scripts();
      }
    },
    []
  );

  const acceptAll = useCallback(() => {
    const state = setConsentState({ tier2: true, tier3: true, method: 'accept_all' });
    applyConsent(state);
  }, [applyConsent]);

  const rejectAll = useCallback(() => {
    const state = setConsentState({ tier2: false, tier3: false, method: 'reject_all' });
    applyConsent(state);
  }, [applyConsent]);

  const saveCustom = useCallback(
    (tier2: boolean, tier3: boolean) => {
      const state = setConsentState({ tier2, tier3, method: 'custom' });
      applyConsent(state);
    },
    [applyConsent]
  );

  const openSettings = useCallback(() => {
    setShowSettings(true);
    setShowBanner(true);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
    // If consent already exists, hide banner too
    if (consent) setShowBanner(false);
  }, [consent]);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        showSettings,
        gpcDetected,
        acceptAll,
        rejectAll,
        saveCustom,
        openSettings,
        closeSettings,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}
